// theme.js
import React from "react";
import { createTheme } from "@mui/material/styles";
import { createMuiTheme } from "@material-ui/core/styles";
import { colors } from "@mui/material";
import Slide from "@mui/material/Slide";
import Fade from "@mui/material/Fade";
import { tokens } from "./tokens";

const colorMap = {
  black: {
    300: "#4A4A4A",
    500: "#111111",
    700: "#050505",
  },
  red: colors.red,
  pink: colors.pink,
  purple: colors.purple,
  deepPurple: colors.deepPurple,
  indigo: colors.indigo,
  blue: colors.blue,
  lightBlue: colors.lightBlue,
  cyan: colors.cyan,
  teal: colors.teal,
  green: colors.green,
  lightGreen: colors.lightGreen,
  lime: colors.lime,
  yellow: colors.yellow,
  amber: colors.amber,
  orange: colors.orange,
  deepOrange: colors.deepOrange,
  brown: colors.brown,
  grey: colors.grey,
  blueGrey: colors.blueGrey,
};

const resolvePrimary = (themeCustom) => {
  const colorById = colorMap[themeCustom?.colorId || "black"];
  const customColor = colorById || themeCustom?.color;
  const main = customColor?.[500] || "#111111";
  const light = customColor?.[300] || "#4A4A4A";
  const dark = customColor?.[700] || "#050505";
  return {
    ...(customColor || {}),
    main,
    light,
    dark,
    contrastText: "#FFFFFF",
  };
};

const MobileDialogTransition = React.forwardRef(function MobileDialogTransition(
  props,
  ref
) {
  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(max-width:600px)").matches;

  if (isMobile) {
    return <Slide ref={ref} direction="up" {...props} />;
  }

  return <Fade ref={ref} {...props} />;
});

export const createAppTheme = (themeCustom = {}) => {
  const mode = themeCustom?.mode === "dark" ? "dark" : "light";
  const isLight = mode === "light";
  const primary = resolvePrimary(themeCustom);
  const roundedPreset = themeCustom?.rounded || "soft";
  const sizePreset = themeCustom?.size || "normal";
  const glass = themeCustom?.glass !== false;
  const baseRadius =
    roundedPreset === "sharp" ? 8 :
      roundedPreset === "round" ? 24 : 16;
  const density =
    sizePreset === "compact" ? 0.9 :
      sizePreset === "large" ? 1.12 : 1;
  const paperBg = isLight ? "#FFFFFF" : "#1A2638";
  const cardBg = isLight ? "#FFFFFF" : "#203047";
  const paperText = isLight ? tokens.colors.textOnLight : tokens.colors.textOnDark;

  const themeOptions = {
    palette: {
      mode,
      type: mode,

      primary,
      secondary: { main: tokens.colors.accentTeal2 },
      error: { main: tokens.colors.error },
      success: { main: tokens.colors.success },

      background: {
        default: isLight ? "#EAF0F8" : tokens.colors.bg0,
        paper: paperBg,
      },

      text: {
        primary: isLight ? "rgba(20, 35, 56, 0.95)" : tokens.colors.textOnDark,
        secondary: isLight ? "rgba(20, 35, 56, 0.65)" : tokens.colors.mutedOnDark,
      },

      divider: isLight ? "rgba(18, 39, 66, 0.18)" : "rgba(205, 220, 240, 0.22)",
    },

    shape: { borderRadius: baseRadius },

    typography: {
      fontFamily: ["Nunito", "Manrope", "Segoe UI", "sans-serif"].join(","),
      h4: { fontWeight: 800, letterSpacing: -0.3 },
      h5: { fontWeight: 800, letterSpacing: -0.25 },
      h6: { fontWeight: 800, letterSpacing: -0.2 },
      body1: { fontWeight: 600, color: isLight ? tokens.colors.textOnLight : tokens.colors.textOnDark },
      body2: { fontWeight: 600, color: isLight ? tokens.colors.mutedOnLight : tokens.colors.mutedOnDark },
      button: { textTransform: "none", fontWeight: 800 },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "@keyframes mobileDialogSlideUp": {
            from: {
              transform: "translateY(100%)",
              opacity: 0.96,
            },
            to: {
              transform: "translateY(0)",
              opacity: 1,
            },
          },
          body: {
            background: isLight
              ? `
              linear-gradient(0deg, rgba(244,236,227,0.48), rgba(244,236,227,0.48)),
              url('/assets/images/mortgage-glass-bg.svg'),
              radial-gradient(900px 600px at 20% 15%, rgba(255,255,255,0.75), transparent 55%),
              radial-gradient(700px 500px at 90% 10%, rgba(244,168,79,0.20), transparent 55%),
              linear-gradient(180deg, #F8F2EA 0%, #EFE6DB 55%, #EFE6DB 100%)
            `
              : `
              linear-gradient(0deg, rgba(9,12,17,0.62), rgba(9,12,17,0.62)),
              url('/assets/images/mortgage-glass-bg.svg'),
              radial-gradient(900px 600px at 20% 15%, rgba(255,255,255,0.06), transparent 55%),
              radial-gradient(700px 500px at 90% 10%, rgba(242,184,75,0.12), transparent 55%),
              linear-gradient(180deg, ${tokens.colors.bg1} 0%, ${tokens.colors.bg0} 55%, ${tokens.colors.bg0} 100%)
            `,
            minHeight: "100vh",
            backgroundSize: "cover, cover, auto, auto, auto",
            backgroundPosition: "center center, center center, center center, center center, center center",
            backgroundAttachment: "fixed, fixed, scroll, scroll, scroll",
            color: isLight ? "rgba(20, 35, 56, 0.95)" : tokens.colors.textOnDark,
          },
        },
      },

      // ✅ Paper claro (como el ejemplo)
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: Math.round(baseRadius * 1.35),
            border: `1px solid ${tokens.colors.borderLight}`,
            background: glass
              ? (isLight ? "rgba(255,255,255,0.72)" : "rgba(24,38,58,0.66)")
              : paperBg,
            color: paperText,
            boxShadow: glass ? tokens.shadow.soft : tokens.shadow.paperLift,
            backdropFilter: glass ? "blur(18px) saturate(120%)" : "none",
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: Math.round(baseRadius * 1.35),
            border: `1px solid ${tokens.colors.borderLight}`,
            background: glass
              ? (isLight ? "rgba(255,255,255,0.78)" : "rgba(32,48,71,0.68)")
              : cardBg,
            color: paperText,
            boxShadow: glass ? tokens.shadow.soft : tokens.shadow.paperLift,
            backdropFilter: glass ? "blur(18px) saturate(120%)" : "none",
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          TransitionComponent: MobileDialogTransition,
          transitionDuration: { enter: 220, exit: 220 },
        },
        styleOverrides: {
          paper: {
            "@media (max-width:600px)": {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              maxHeight: "100%",
              borderRadius: 0,
            },
          },
          paperFullScreen: {
            "@media (max-width:600px)": {
              borderRadius: 0,
            },
          },
        },
      },

      // Para secciones internas tipo “tarjeta teal” del ejemplo
      MuiCardContent: {
        styleOverrides: {
          root: {
            // nada por defecto; esto lo aplicas por sx cuando quieras “teal block”
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: Math.round(baseRadius * 0.95),
            padding: `${Math.round(10 * density)}px ${Math.round(16 * density)}px`,
            minHeight: Math.round(40 * density),
          },
          containedPrimary: {
            background: `linear-gradient(180deg, ${primary.light}, ${primary.main})`,
            color: "#FFFFFF",
            boxShadow: "0 14px 26px rgba(0,0,0,0.35)",
          },
          outlined: {
            borderColor: tokens.colors.borderLight,
            color: tokens.colors.textOnLight,
            borderRadius: Math.round(baseRadius * 0.9),
            background: isLight ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.55)",
          },
        },
      },

      MuiTextField: {
        defaultProps: { variant: "outlined" },
      },

      // ✅ Input pensado para estar dentro de Paper claro
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: Math.round(baseRadius * 0.9),
            background: isLight ? "rgba(255,255,255,0.96)" : "rgba(18,30,47,0.82)",
            boxShadow: tokens.shadow.insetLight,
            color: paperText,
            "& fieldset": { borderColor: isLight ? tokens.colors.borderLight : "rgba(205,220,240,0.30)" },
            "&:hover fieldset": { borderColor: isLight ? "rgba(10, 35, 55, 0.18)" : "rgba(205,220,240,0.44)" },
            "&.Mui-focused fieldset": { borderColor: isLight ? "rgba(242,184,75,0.55)" : "rgba(242,184,75,0.70)" },
          },
          input: { padding: `${Math.round(14 * density)}px ${Math.round(16 * density)}px` },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: `${Math.round(10 * density)}px ${Math.round(12 * density)}px`,
            border: 'none',
            // backgroundColor: 'red'
          },


        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            padding: `${Math.round(10 * density)}px ${Math.round(12 * density)}px`,
            border: 'none',
            
            // backgroundColor: 'red'
          },
          paperBg: {
            borderRadius: Math.round(baseRadius * 1.35),
            border: `1px solid ${tokens.colors.borderLight}`,
            background: glass
              ? (isLight ? "rgba(255,255,255,0.72)" : "rgba(24,38,58,0.66)")
              : paperBg,
            color: paperText,
            boxShadow: glass ? tokens.shadow.soft : tokens.shadow.paperLift,
            backdropFilter: glass ? "blur(14px)" : "none",
          }
          

        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isLight ? "rgba(18,39,66,0.18)" : "rgba(205, 220, 240, 0.22)",
          },
        },
      },

      // ✅ Bottom bar: vidrio oscuro (shell), como iOS
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            background: isLight ? "rgba(255,255,255,0.75)" : "rgba(15, 35, 48, 0.55)",
            backdropFilter: "blur(14px)",
            borderTop: isLight ? "1px solid rgba(24,40,63,0.15)" : "1px solid rgba(255,255,255,0.10)",
            height: 70,
            paddingBottom: 8,
          },
        },
      },

      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: isLight ? "rgba(24,40,63,0.60)" : "rgba(255,255,255,0.60)",
            "&.Mui-selected": { color: primary.main },
          },
          label: { fontWeight: 800 },
        },
      },
    },
  };

  // Compatibilidad MUI v4 (makeStyles/@material-ui/core)
  themeOptions.overrides = {
    MuiPaper: {
      root: {
        borderRadius: Math.round(baseRadius * 1.35),
        border: `1px solid ${tokens.colors.borderLight}`,
        background: glass
          ? (isLight ? "rgba(255,255,255,0.72)" : "rgba(24,38,58,0.66)")
          : paperBg,
        color: paperText,
        boxShadow: glass ? tokens.shadow.soft : tokens.shadow.paperLift,
      },
    },
    MuiCard: {
      root: {
        borderRadius: Math.round(baseRadius * 1.35),
        border: `1px solid ${tokens.colors.borderLight}`,
        background: glass
          ? (isLight ? "rgba(255,255,255,0.78)" : "rgba(32,48,71,0.68)")
          : cardBg,
        color: paperText,
        boxShadow: glass ? tokens.shadow.soft : tokens.shadow.paperLift,
      },
    },
    MuiButton: {
      root: {
        borderRadius: Math.round(baseRadius * 0.95),
        minHeight: Math.round(40 * density),
      },
      containedPrimary: {
        background: `linear-gradient(180deg, ${primary.light}, ${primary.main})`,
        color: "#FFFFFF",
      },
      outlined: {
        borderRadius: Math.round(baseRadius * 0.9),
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: Math.round(baseRadius * 0.9),
      },
      input: {
        padding: `${Math.round(14 * density)}px ${Math.round(16 * density)}px`,
      },
    },
    MuiDialog: {
      paper: {
        "@media (max-width:600px)": {
          margin: 0,
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          maxHeight: "100%",
          borderRadius: 0,
          animation: "mobileDialogSlideUp 220ms ease-out",
        },
      },
      paperFullScreen: {
        "@media (max-width:600px)": {
          borderRadius: 0,
        },
      },
    },
    // MuiTableCell: {
    //   root: {
    //     padding: `${Math.round(10 * density)}px ${Math.round(12 * density)}px`,

    //   },
    // },
  };

  return createTheme(themeOptions);
};

export const createLegacyAppTheme = (themeCustom = {}) => {
  const v5Theme = createAppTheme(themeCustom);
  return createMuiTheme(v5Theme);
};

export const theme = createAppTheme({ mode: "dark" });
